
import React, { useState, useEffect } from 'react';
import { Sparkles, X, Check, Info } from 'lucide-react';
import { Language } from '../../types';
import { AIDraftResult } from '../../services/gemini';
import { t } from '../../i18n';

interface AIDraftModalProps {
    isOpen: boolean;
    isAnimating: boolean;
    draft: AIDraftResult | null;
    onClose: () => void;
    onApply: () => void;
    lang: Language;
}

export const AIDraftModal: React.FC<AIDraftModalProps> = ({ isOpen, isAnimating, draft, onClose, onApply, lang }) => {
    const [localDraft, setLocalDraft] = useState<AIDraftResult | null>(null);

    // 프롭으로 전달된 draft가 있으면 로컬 상태에 저장 (애니메이션 중 유지용)
    useEffect(() => {
        if (draft) {
            setLocalDraft(draft);
        }
    }, [draft]);

    // 렌더링 시에는 프롭이나 로컬 상태 중 하나라도 있으면 내용을 표시함
    const displayDraft = draft || localDraft;

    if (!isOpen || !displayDraft) return null;

    return (
        <div className={`fixed inset-0 z-[250] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-500 ${isAnimating ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}></div>
            <div className={`relative bg-white w-full h-[90vh] md:h-[85vh] md:max-w-2xl md:mx-auto rounded-t-[48px] md:rounded-[48px] shadow-2xl flex flex-col overflow-hidden transition-transform duration-700 ease-[0.32,0.72,0,1] ${isAnimating ? 'translate-y-0 md:scale-100' : 'translate-y-full md:scale-95'}`}>
                
                <div className="w-12 h-1.5 bg-slate-100 rounded-full mx-auto my-4 shrink-0 md:hidden"></div>

                <header className="px-6 py-5 md:px-8 md:py-6 flex items-center justify-between border-b border-gray-100 bg-white sticky top-0 z-10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-500 flex items-center justify-center shadow-sm">
                            <Sparkles size={22} />
                        </div>
                        <div>
                            <h3 className="text-lg font-black text-slate-900 uppercase tracking-tight leading-none">{t(lang, 'aiDraftTitle')}</h3>
                            <p className="text-[10px] font-black text-amber-500 uppercase tracking-widest mt-1">{t(lang, 'aiDraftSubtitle')}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="p-2.5 bg-gray-50 text-slate-400 md:hover:text-slate-900 rounded-full transition-all">
                            <X size={20} strokeWidth={3} />
                        </button>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto p-6 md:p-8 custom-scrollbar">
                    <div className="mb-8">
                        <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-2 block">{t(lang, 'extractedTitle')}</span>
                        <h4 className="text-xl font-black text-slate-900 leading-tight uppercase tracking-tight">{displayDraft.title}</h4>
                    </div>
                    
                    <div>
                        <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-2 block">{t(lang, 'generatedContent')}</span>
                        <div className="prose prose-slate max-w-none">
                            <p className="text-slate-600 text-base md:text-lg leading-relaxed whitespace-pre-wrap font-medium">
                                {displayDraft.generatedContent}
                            </p>
                        </div>
                    </div>
                    
                    <div className="mt-10 p-6 bg-indigo-50/50 rounded-3xl border border-indigo-100 flex items-start gap-4">
                        <Info size={20} className="text-indigo-400 shrink-0 mt-0.5" />
                        <p className="text-[12px] font-medium text-indigo-600/80 leading-relaxed uppercase tracking-wide">
                            {t(lang, 'aiDraftDisclaimer')}
                        </p>
                    </div>
                    <div className="h-10" />
                </div>

                <footer className="p-6 md:p-8 border-t border-gray-100 bg-white flex gap-3">
                    <button 
                        onClick={onClose}
                        className="flex-1 py-4 text-xs font-black text-slate-400 uppercase tracking-[0.2em] md:hover:text-slate-900 transition-colors"
                    >
                        {t(lang, 'cancel')}
                    </button>
                    <button 
                        onClick={onApply}
                        className="flex-[2] bg-indigo-600 text-white py-4 rounded-[20px] font-black text-xs uppercase tracking-[0.2em] shadow-md active:scale-95 flex items-center justify-center gap-3 transition-all md:hover:bg-indigo-700"
                    >
                        <Check size={18} /> {t(lang, 'applyToEditor')}
                    </button>
                </footer>
            </div>
        </div>
    );
};
